import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, UserCircle, Mail, Users, Calendar, CreditCard, BarChart3, CheckCircle } from 'lucide-react';

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
  userName?: string;
}

export default function WelcomeModal({ open, onClose, userName }: WelcomeModalProps) {
  const features = [
    {
      icon: Building2,
      title: 'Create & Manage Units',
      description: 'Set up your unit and manage members, events, and activities all in one place.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Calendar,
      title: 'Organize Events',
      description: 'Create dining events, festive boards, and gatherings with ease. Track RSVPs and attendance.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: Mail,
      title: 'Respond to Invitations',
      description: 'Receive event invitations from other members and respond quickly with your meal preferences.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: UserCircle,
      title: 'Update Your Profile',
      description: 'Keep your profile up to date with your rank, unit affiliations, and dietary requirements.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      icon: Users,
      title: 'Join Multiple Units',
      description: 'Be part of multiple units and manage all your memberships from one dashboard.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
    {
      icon: CreditCard,
      title: 'Manage Payments',
      description: 'Pay for events securely online and track your payment history in one place.',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      icon: BarChart3,
      title: 'View Analytics',
      description: 'Track attendance, payments, and event statistics for your unit.',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
    },
    {
      icon: CheckCircle,
      title: 'QR Code Check-In',
      description: 'Fast and easy guest check-in at events using QR codes on mobile devices.',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Dining Steward Logo" 
              className="h-16 w-16"
            />
          </div>
          <DialogTitle className="text-center text-3xl">
            Welcome to Dining Steward{userName ? `, ${userName}` : ''}!
          </DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            Your all-in-one platform for managing masonic dining events and unit activities
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          <h3 className="text-lg font-semibold mb-4 text-center">What You Can Do</h3>
          
          <div className="grid gap-4 md:grid-cols-2">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-l-4 hover:shadow-md transition-shadow" style={{ borderLeftColor: feature.color.replace('text-', '') }}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${feature.bgColor}`}>
                        <Icon className={`h-6 w-6 ${feature.color}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1">{feature.title}</h4>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>

        <div className="flex justify-center pt-6">
          <Button onClick={onClose} size="lg" className="min-w-[200px]">
            Continue to Dashboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

